import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { spesa } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { RoleRouteObject, RouteFunctionParams } from '@/types'
import { redirect } from 'react-router-dom'
import { defer, ActionFunction, LoaderFunction } from 'react-router-typesafe'
import { getAzureUserInformation } from '@/auth/azure/azureManager'
import { Role, Roles } from '@/auth/azure/Roles'
import { ODataClientError } from '@odata2ts/http-client-api'
import ExpenseEditor from '@/routes/expenses/ExpenseEditor'
import { TipologiaSpesa } from '@/routes/expenses/ExpenseEditor.types'

export interface ExpenseWithFile extends spesa {
  file?: File | null
}

export const expenseEditorRouteLoader = (async ({ request, params }: RouteFunctionParams<'expenseId'>) => {
  const expenseId = params.expenseId
  const dateSearchParam: string | null = new URL(request.url).searchParams.get('date')
  const employeeIdSearchParam: string | null = new URL(request.url).searchParams.get('employeeId')

  let title

  const qsAdminApi = useQsAdminApiManager.getState().service
  const userInfo = await getAzureUserInformation()

  let dateExpense = new Date()
  if (dateSearchParam) {
    let dateSP: Date | null = null
    dateSP = new Date(dateSearchParam)
    dateExpense = dateSP
  }

  let expense: ExpenseWithFile

  if (expenseId) {
    //editor
    const getExpense = await qsAdminApi
      .spesa(Number(expenseId))
      .query((builder, spesa) => {
        builder.expanding('tipologia', (tipologiaBuilder, tipologia) => {
          tipologiaBuilder.select('id', 'nome')
        })
        builder.expanding('impiegato', (impiegatoBuilder, impiegato) => {
          impiegatoBuilder.select('id', 'nome', 'email')
        })
      })
      .catch((error: ODataClientError) => {
        if (error.status === 404) throw new Error('404 Not Found: Spesa non trovata')
        throw new Error()
      })
    expense = getExpense.data satisfies spesa
    title = `${expense.ded_Dis}`

    if (expense.impiegato?.email !== userInfo?.email) {
      const allowedRoles = [Roles.Administrator, Roles.GlobalAdministrator]
      const isCurrentUserAllowed = allowedRoles.some((role: Role) => userInfo?.roles?.hasRole(role))
      if (!isCurrentUserAllowed) {
        throw new Error(`La spesa ${expense.id} non appartiene all'utente corrente. Impossibile proseguire`)
      }
    }
  } else {
    //creator
    title = `Nuova spesa`

    const autore = await qsAdminApi.user().query((builder, impiegato) => {
      if (employeeIdSearchParam) builder.filter(impiegato.id.eq(Number(employeeIdSearchParam)))
      else builder.filter(impiegato.email.eq(userInfo?.email ?? ''))
      builder.expanding('reparto', (repartoBuilder, reparto) => {
        repartoBuilder.select('id', 'nome')
      })
      builder.select('id', 'fullname', 'reparto', 'email')
    })
    const tipologiaDefault = await qsAdminApi.tipologia_spesa(TipologiaSpesa.VITTO).query((builder, spesa) => {
      builder.select('id', 'nome')
    })

    expense = {
      id: 0,
      ded_Dis: '',
      ded_RootFam: '',
      ded_SubFam: '',
      data: dateExpense.toISOString(),
      totale: 0,
      note: null,
      impiegato: autore.data.value[0],
      tipologia: tipologiaDefault.data,
      anno_rif: dateExpense.getFullYear(),
      mainDocumentUrl: '',
      sLocalDateShort: '',
      attachmentsFolderUrl: '',
      file: null,
    }
  }

  const getEmployees = qsAdminApi.user().query((builder, employee) => {
    builder.select('id', 'fullname')
    builder.orderBy(employee.fullname.asc())
  })

  const getTypes = qsAdminApi.tipologia_spesa().query((builder, spesa) => {
    builder.select('id', 'nome')
    builder.orderBy(spesa.nome.asc())
  })

  return defer({
    title,
    expense,
    userInfo,
    getEmployees,
    getTypes,
    defaultCRUDAllowedRoles: [
      Roles.GlobalAdministrator,
      Roles.Administrator,
      Roles.Supervisor,
      Roles.Sales,
      Roles.Marketing,
      Roles.TechDeveloper,
    ],
  })
}) satisfies LoaderFunction

export const expenseEditorRouteAction = (async ({ request }: RouteFunctionParams) => {
  const expenseFormData = await request.formData()
  const client = useQsAdminApiManager.getState().client

  switch (request.method) {
    case 'POST': {
      const res = await client.post('/api/spesa/createupdate', expenseFormData)
      return redirect(`../${res.data.id}`)
    }
    case 'PUT':
    case 'PATCH': {
      const res = await client.put('/api/spesa/createupdate', expenseFormData)
      return res
    }
    default: {
      throw new Response('Method not allowed', {
        status: 405,
        statusText: 'Method not allowed',
      })
    }
  }
}) satisfies ActionFunction

const ExpenseEditorRoute = {
  path: ':expenseId',
  element: <ExpenseEditor creating={false} />,
  loader: expenseEditorRouteLoader,
  action: expenseEditorRouteAction,
  allowedRoles: [
    Roles.GlobalAdministrator,
    Roles.Administrator,
    Roles.Marketing,
    Roles.TechDeveloper,
    Roles.Sales,
    Roles.Supervisor,
  ],
  handle: {
    crumb: (match) => ({
      label: match.data.expense.ded_Dis,
    }),
  },
} as RoleRouteObject

export default ExpenseEditorRoute
